/**
 * 租户工具函数
 * 用于租户检测、切换和配置管理
 */

import { TENANTS, TenantConfigWithId, getTenantById, getDefaultTenant } from '@/config/tenants.config'
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { Capacitor } from '@capacitor/core'

/**
 * LocalStorage 中存储租户 ID 的 key
 */
const TENANT_STORAGE_KEY = 'tenant_id'

/**
 * 检测是否为原生App环境
 */
function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * 从URL获取租户ID
 */
function getTenantIdFromUrl(): any {

  // 尝试从 hash 中获取参数（应对 http://domain.com/#/login?orgid=xxx 这种形式）
  const hash = window.location.hash;
  if (hash.includes('?')) {
    const hashQuery = hash.split('?')[1] || '';
    const hashParams = new URLSearchParams(hashQuery);
    const hashOrgId = hashParams.get('orgid') || hashParams.get('orgId');
    if (hashOrgId) return hashOrgId;
  }

  // // 2. 兜底处理：有些形式可能是 #/login/xxx?orgid=...
  const match = hash.match(/[?&]orgid=([^&?]+)/i);
  if (match) return match[1];

  return null;
}

/**
 * 获取当前租户配置
 * 
 * 优先级策略：
 * - App环境：localStorage 优先（不受URL影响）
 * - Web/小程序环境：URL 优先，然后是 localStorage
 * 
 * 具体逻辑：
 * 1. App环境：
 *    - localStorage 中的租户ID
 *    - 默认租户
 * 
 * 2. Web/小程序环境：
 *    - URL 中的 orgid 参数（优先级最高，并同步到 localStorage）
 *    - localStorage 中的租户ID
 *    - 默认租户
 * 
 * @returns 当前租户配置
 */
export function getCurrentTenant(): any {
  try {
    const isApp = isNativeApp()

    // ========== App环境：localStorage 优先 ==========
    if (isApp) {
      console.log('[Tenant] App环境，使用 localStorage 优先策略')

      // 1. 从 localStorage 读取 (仅尝试 mobile 内部的 tenant_id)
      const savedTenantId = getsubKey('mobile', TENANT_STORAGE_KEY)

      if (savedTenantId) {
        const tenant = getTenantById(savedTenantId)
        if (tenant) {
          console.log(`[Tenant] 使用已保存的租户: ${tenant.name} (${tenant.id})`)
          return tenant
        }
      }

      // 2. 返回默认租户
      const defaultTenant = getDefaultTenant()
      saveTenantId(defaultTenant.id)
      console.log(`[Tenant] 使用默认租户: ${defaultTenant.name} (${defaultTenant.id})`)
      return defaultTenant
    }

    // ========== Web/小程序环境：URL 优先 ==========
    console.log('[Tenant] Web/小程序环境，使用 URL 优先策略')

    // debugger
    // 1. 优先从 URL 读取 orgid 参数
    const urlOrgId = getTenantIdFromUrl()
    console.log('[Tenant] URL中的orgid:', urlOrgId)
    if (urlOrgId) {
      const tenant = getTenantById(urlOrgId)
      if (tenant) {
        // URL 参数有效，同步到 localStorage
        saveTenantId(tenant.id)
        console.log(`[Tenant] 使用 URL 参数租户: ${tenant.name} (${tenant.id})`)
        return tenant
      }
    }

    // 2. URL 无参数或无效，尝试从 localStorage 读取
    const savedTenantId = getsubKey('mobile', TENANT_STORAGE_KEY)

    if (savedTenantId) {
      const tenant = getTenantById(savedTenantId)
      if (tenant) {
        console.log(`[Tenant] 使用已保存的租户: ${tenant.name} (${tenant.id})`)
        return tenant
      }
    }

    // 3. 返回默认租户
    const defaultTenant = getDefaultTenant()
    saveTenantId(defaultTenant.id)
    console.log(`[Tenant] 使用默认租户: ${defaultTenant.name} (${defaultTenant.id})`)
    return defaultTenant

  } catch (error) {
    console.error('[Tenant] 获取租户配置失败:', error)
    return getDefaultTenant()
  }
}

/**
 * 保存租户 ID 到 localStorage
 * @param tenantId 租户 ID
 */
export function saveTenantId(tenantId: string): void {
  try {
    updateKeyJson('mobile', TENANT_STORAGE_KEY, tenantId)
  } catch (error) {
    console.error('[Tenant] 保存租户 ID 失败:', error)
  }
}

/**
 * 更新URL中的租户参数
 * @param tenantId 租户ID
 */
function updateUrlTenantParam(tenantId: string): void {
  try {
    const isApp = isNativeApp()
    // App环境不需要更新URL
    if (isApp) {
      console.log('[Tenant] App环境，跳过URL更新')
      return
    }

    // Web/小程序环境：更新URL参数
    const hash = window.location.hash
    const [path, query] = hash.split('?')
    const params = new URLSearchParams(query || '')

    // 设置或更新 orgid 参数 
    params.set('orgid', tenantId)

    // 构建新的URL
    const newHash = `${path}?${params.toString()}`

    // 使用 replaceState 更新URL，不触发页面跳转
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`)

    console.log(`[Tenant] URL已更新: orgid=${tenantId}`)
  } catch (error) {
    console.error('[Tenant] 更新URL失败:', error)
  }
}

/**
 * 切换租户
 * @param tenantId 目标租户 ID
 * @param reload 是否刷新页面（默认 true）
 */
export function switchTenant(tenantId: string, reload: boolean = true): void {
  const tenant = getTenantById(tenantId)

  if (!tenant) {
    console.error(`[Tenant] 租户不存在: ${tenantId}`)
    throw new Error(`租户 ${tenantId} 不存在`)
  }

  // 保存到 localStorage
  saveTenantId(tenantId)

  // Web/小程序环境：同步更新URL参数
  updateUrlTenantParam(tenantId)

  console.log(`[Tenant] 切换到租户: ${tenant.name} (${tenant.id})`)

  // 刷新页面以应用新租户配置
  if (reload) {
    window.location.reload()
  }
}




/**
 * 应用租户主题
 * 将租户的主题配置应用到 CSS 变量
 */
export function applyTenantTheme(): void {
  const tenant = getCurrentTenant()
  const root = document.documentElement

  // 设置 CSS 变量
  root.style.setProperty('--color-primary', tenant.theme.primaryColor)
  root.style.setProperty('--color-background', tenant.theme.backgroundColor)

  // 如果有渐变色配置
  if (tenant.theme.gradientColors && tenant.theme.gradientColors.length >= 2) {
    const gradient = `linear-gradient(180deg, ${tenant.theme.gradientColors.join(', ')})`
    root.style.setProperty('--gradient-background', gradient)
  }

  console.log(`[Tenant] 应用主题: ${tenant.name}`)
}

/**
 * 检查当前租户是否支持某个功能
 * @param feature 功能名称
 * @returns 是否支持
 */
export function isTenantFeatureEnabled(feature: keyof TenantConfigWithId['features']): boolean {
  const tenant = getCurrentTenant()
  return tenant.features[feature] || false
}
